import React from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@lib/utils.ts';

interface TradeData {
  date: string;
  accountBalance: number;
  netPl: number;
}

type TradePayload = {
  value: number;
  payload?: TradeData;
  color?: string;
};

const mockData: TradeData[] = [
  { date: '2025-08-26T00:00:00.000Z', accountBalance: 10, netPl: 15 },
  { date: '2025-08-27T00:00:00.000Z', accountBalance: 12, netPl: -2 },
  { date: '2025-08-28T00:00:00.000Z', accountBalance: 11, netPl: 10 },
  { date: '2025-08-29T00:00:00.000Z', accountBalance: 13, netPl: -5 },
  { date: '2025-08-30T00:00:00.000Z', accountBalance: 9, netPl: 5 },
  { date: '2025-09-03T00:00:00.000Z', accountBalance: 14, netPl: -10 },
  { date: '2025-09-04T00:00:00.000Z', accountBalance: 11, netPl: 20 },
  { date: '2025-09-05T00:00:00.000Z', accountBalance: 15, netPl: 10 },
];

interface TooltipProps {
  active?: boolean;
  payload?: TradePayload[];
}

const CustomTooltip: React.FC<TooltipProps> = ({ active, payload }) => {
  const { t } = useTranslation();

  if (active && payload && payload.length) {
    return (
      <div className={'bg-[#222] p-2.5 rounded-1 text-xs'}>
        <p>{`${t('dashboard.dateTooltip')} ${formatDate(payload[0].payload?.date)}`}</p>
        <p>{`${t('dashboard.accountBalance')}: ${payload[0]?.payload?.accountBalance}`}</p>
        <p>{`${t('dashboard.netPl')}: ${payload[0]?.payload?.netPl}`}</p>
      </div>
    );
  }

  return null;
};

interface CustomLegendProps {
  payload?: TradePayload[];
}

const CustomLegend: React.FC<CustomLegendProps> = ({ payload }) => {
  const { t } = useTranslation();

  if (!payload) return null;

  const newPayload = [payload[payload.length - 1], ...payload.slice(0, -1)];

  return (
    <div className={'flex flex-col items-center'}>
      <p className={'text-xs font-medium'}>{t('dashboard.tradeChartTitle')}</p>
      <div className={'flex gap-x-3 p-2.5'}>
        {newPayload.map((entry, index) => (
          <div key={index} className={'flex items-center gap-x-1.5'}>
            <div
              className={`w-9 h-3`}
              style={{ backgroundColor: entry.color }}
            />
            <p className={'text-xs font-medium'}>{entry.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

interface TradeChartProps {
  data?: TradeData[];
}

export const TradeChart: React.FC<TradeChartProps> = ({ data = mockData }) => {
  const { t } = useTranslation();

  return (
    <div className={'flex w-full flex-col'}>
      <ResponsiveContainer height={500} width="100%">
        <ComposedChart
          data={data}
          // margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <CartesianGrid
            stroke="#CECECE"
            strokeDasharray="0"
            vertical={false}
          />

          <XAxis
            dataKey="date"
            dy={10}
            fontSize={8}
            padding={{ left: 0, right: 0 }}
            stroke="#CECECE"
            tickFormatter={(date) => formatDate(date)}
            tickLine={false}
          />
          <YAxis
            fontSize={8}
            stroke="#CECECE"
            tickLine={false}
            yAxisId="left"
          />
          <YAxis
            domain={data.length > 0 ? [0, 'auto'] : [-30, 30]}
            fontSize={8}
            orientation="right"
            stroke="#CECECE"
            tickLine={false}
            yAxisId="right"
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            align="center"
            content={<CustomLegend />}
            layout="horizontal"
            verticalAlign="top"
          />
          {data.length < 1 && (
            <Bar
              barSize={36}
              dataKey=""
              fill="#14CA7400"
              name={''}
              stackId="a"
              yAxisId="left"
            />
          )}

          <Bar
            barSize={36}
            dataKey="netPl"
            fill="#C72E2E"
            name={t('dashboard.netPl')}
            yAxisId="left"
          >
            {data?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.netPl >= 0 ? '#14CA74' : '#C72E2E'}
              />
            ))}
          </Bar>
          <Line
            dataKey="accountBalance"
            dot={{ r: 4, fill: '#3490FF' }}
            name={t('dashboard.accountBalance')}
            stroke="#3490FF"
            strokeWidth={2}
            type="monotone"
            yAxisId="right"
          />
        </ComposedChart>
      </ResponsiveContainer>
      <div className={'flex text-xs font-medium justify-around'}>
        <p>{t('dashboard.accountAddons')}</p>
        <p>{t('dashboard.accountPayout')}</p>
      </div>
    </div>
  );
};
